import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { GlassButton } from '@/components/ui/GlassButton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';

// Cloudinary Base URL with q_90 transformation
const CLOUDINARY_BASE = "https://res.cloudinary.com/dx0aqgtyg/image/upload/q_10";

const INTRO_SLIDES = [
    {
        image: `${CLOUDINARY_BASE}/v1766233669/oreogImageHero_opcwyr.png`,
        title: 'Welcome to Chitra AI',
        description: 'Transform your ideas into stunning images with AI. Experience the power of creative visualization at your fingertips.',
    },
    {
        image: `${CLOUDINARY_BASE}/v1766233678/oreogImage1_ybehgo.png`,
        title: 'Powerful Creative Tools',
        description: 'Upload images, use video mode, access quick tools, or draw on canvas. Select your perfect aspect ratio for any project.',
    },
    {
        image: `${CLOUDINARY_BASE}/v1766233668/oreogImage4_du9vf8.png`,
        title: 'Choose Your Style',
        description: 'Select from multiple artistic styles including Realistic, Anime, 3D, Ghibli, and more to bring your vision to life.',
    },
    {
        image: `${CLOUDINARY_BASE}/v1766233678/oreogImage2_qtivrr.png`,
        title: 'Explore Your Gallery',
        description: 'Browse through your AI-generated masterpieces. Switch between all images, your uploads, and videos with ease.',
    },
    {
        image: `${CLOUDINARY_BASE}/v1766233668/oreogImage3_hjr9si.png`,
        title: 'Share Your Creations',
        description: 'Download, share, and showcase your AI artwork. Every generation is saved in your personal gallery.',
    },
];

interface ChitraIntroModalProps {
    isDarkMode: boolean;
}

const CHITRA_INTRO_SHOWN_KEY = 'chitra_intro_shown';

export const ChitraIntroModal = ({ isDarkMode }: ChitraIntroModalProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        try {
            const hasSeenIntro = localStorage.getItem(CHITRA_INTRO_SHOWN_KEY);

            if (hasSeenIntro !== 'true') {
                setIsVisible(true);
                localStorage.setItem(CHITRA_INTRO_SHOWN_KEY, 'true');
            }
        } catch (error) {
            console.error('Error accessing localStorage:', error);
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            lockBodyScroll();
            return () => {
                unlockBodyScroll();
            };
        }
    }, [isVisible]);

    if (!isVisible) return null;

    const onClose = () => {
        setIsVisible(false);
        unlockBodyScroll();
    };

    const nextSlide = () => {
        if (currentSlide < INTRO_SLIDES.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const slide = INTRO_SLIDES[currentSlide];

    const baseSurface = isDarkMode
        ? 'bg-[#303030]/80 text-slate-100 border-white/10 backdrop-blur-xl supports-[backdrop-filter]:bg-[#303030]/60'
        : 'bg-white/80 text-slate-900 border-gray-200/60 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60';

    const backdropClass = isDarkMode
        ? 'bg-black/60'
        : 'bg-white';

    return (
        <div className={`fixed inset-0 ${backdropClass} backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300`}>
            <div className={`relative w-full max-w-3xl max-h-[92vh] rounded-3xl overflow-hidden border shadow-2xl ${baseSurface} animate-in zoom-in-95 duration-300`}>
                <div className={`relative w-full h-[200px] sm:h-[300px] overflow-hidden ${isDarkMode
                    ? 'bg-[#303030]/50 backdrop-blur-lg'
                    : 'bg-white/50 backdrop-blur-lg'
                    }`}>
                    <img
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-full object-cover transition-opacity duration-500"
                        key={currentSlide}
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 backdrop-blur-md bg-black/20 px-4 py-2 rounded-full">
                        {INTRO_SLIDES.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide
                                    ? 'w-8 bg-white shadow-lg shadow-white/50'
                                    : 'w-2 bg-white/40 hover:bg-white/60'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>

                <div className="p-4 sm:p-10 space-y-2 sm:space-y-3">
                    <h2 className={`text-2xl sm:text-4xl font-bold bg-clip-text text-transparent py-1 ${isDarkMode
                        ? 'bg-gradient-to-r from-white to-slate-300'
                        : 'bg-gradient-to-r from-slate-900 to-slate-700'
                        }`}>
                        {slide.title}
                    </h2>

                    <p className={`text-base sm:text-xl leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'
                        }`}>
                        {slide.description}
                    </p>

                    <div className="flex items-center justify-between pt-2">
                        <Button
                            onClick={prevSlide}
                            disabled={currentSlide === 0}
                            variant="outline"
                            className={`flex items-center gap-2 h-10 sm:h-12 px-4 sm:px-6 rounded-full ${isDarkMode
                                ? 'border-[#3f3f46] bg-transparent text-slate-200 hover:bg-[#3f3f46] disabled:opacity-20'
                                : 'border-gray-300 bg-white text-slate-800 hover:bg-slate-100 disabled:opacity-20'
                                } transition-all disabled:cursor-not-allowed`}
                        >
                            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <div className="flex justify-center">
                            <GlassButton onClick={currentSlide === INTRO_SLIDES.length - 1 ? onClose : nextSlide} isDarkMode={isDarkMode}>
                                {currentSlide === INTRO_SLIDES.length - 1 ? (
                                    'Get Started'
                                ) : (
                                    <>
                                        Next
                                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                                    </>
                                )}
                            </GlassButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
